import axios, { AxiosError } from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { API_BASE_URL } from '@/config'
import { msg } from './msgHolder'
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './token'
import { refreshToken } from '@/api/admin'

// 待重试请求队列
interface PendingRequest {
  resolve: (value: unknown) => void
  reject: (reason?: unknown) => void
  config: InternalAxiosRequestConfig
}

let isRefreshing = false
const pendingRequests: PendingRequest[] = []

// 创建 axios 实例
const request: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
})

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 添加 Token
    const accessToken = getAccessToken()
    const refreshTokenValue = getRefreshToken()

    if (accessToken) {
      config.headers['Token-Access'] = accessToken
    }
    if (refreshTokenValue) {
      config.headers['Token-Refresh'] = refreshTokenValue
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  (resp) => {
    // 直接返回响应数据
    return resp
  },
  async (error: AxiosError) => {
    // 统一错误响应格式（让组件永远不需要 try/catch）
    const errorResponse = (msg: string) => ({
      data: { code: -1, msg, data: null },
    })

    // 网络错误处理
    if (!error.response) {
      msg().error('网络错误，换个网络试试', 4)
      return errorResponse('网络错误')
    }

    const { status, config, data } = error.response

    // 401 处理：Token 过期，需要刷新
    if (status === 401) {
      // 如果当前请求已经添加了 X-Skip-Auth-Refresh 头，说明是刷新 Token 的请求本身
      if ((config?.headers as Record<string, string>)?.['X-Skip-Auth-Refresh'] === '1') {
        clearTokens()
        window.location.href = '/login'
        return errorResponse('登录已过期')
      }

      // 如果正在刷新 Token，将当前请求加入等待队列
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({
            resolve,
            reject,
            config: config as InternalAxiosRequestConfig,
          })
        })
      }

      // 开始刷新 Token
      isRefreshing = true
      const refreshTokenValue = getRefreshToken()

      if (!refreshTokenValue) {
        clearTokens()
        isRefreshing = false
        window.location.href = '/login'
        return errorResponse('登录已过期')
      }

      try {
        const resp = await refreshToken()

        if (
          resp?.code === 0 &&
          resp.data?.access_token &&
          resp.data?.refresh_token
        ) {
          // 刷新成功，保存新 Token
          setTokens(resp.data.access_token, resp.data.refresh_token)

          // 更新原请求的 Token
          if (config?.headers) {
            config.headers['Token-Access'] = resp.data.access_token
            config.headers['Token-Refresh'] = resp.data.refresh_token
          }

          // 重新发起原请求
          const retryResponse = await request(config as InternalAxiosRequestConfig)

          // 处理等待队列中的所有请求
          pendingRequests.forEach(({ resolve, config: pendingConfig }) => {
            if (pendingConfig.headers) {
              pendingConfig.headers['Token-Access'] = resp.data.access_token
              pendingConfig.headers['Token-Refresh'] = resp.data.refresh_token
            }
            resolve(request(pendingConfig))
          })

          pendingRequests.length = 0
          isRefreshing = false
          return retryResponse
        } else {
          clearTokens()
          isRefreshing = false
          pendingRequests.forEach(({ reject }) => reject(new Error('刷新令牌失败')))
          pendingRequests.length = 0
          window.location.href = '/login'
          return errorResponse('登录已过期')
        }
      } catch (refreshError) {
        clearTokens()
        isRefreshing = false
        pendingRequests.forEach(({ reject }) => reject(refreshError))
        pendingRequests.length = 0
        window.location.href = '/login'
        return errorResponse('登录已过期')
      }
    }

    // 4xx/5xx：有 ApiResponse 结构 → 拦截器统一弹错误提示，resolve 给组件判断 code
    if (data && typeof data === 'object' && 'code' in data && 'msg' in data) {
      const apiData = data as { msg: string }
      msg().error(apiData.msg, 4)
      return error.response
    }

    // 无 ApiResponse 结构的未知错误
    msg().error('请求失败，请稍后重试', 4)
    return errorResponse('请求失败')
  }
)

export default request
